/**
 * OTP verification action creator (CC-03 / D-11).
 *
 * Posts `{otpSessionToken, code}` to `/api/v1/auth/otp/verify`. Backend mints
 * the access token HERE — never in the login step when OTP is required. This
 * closes any XSS/MITM window between login and OTP entry (D-11 security
 * boundary).
 *
 * On success: returns `{snapshot}` identical in shape to login's Response A.
 * On failure: throws ApiError-shaped object with OTP_INVALID / OTP_EXPIRED.
 *
 * `startOtp` is NOT implemented as a separate network call: the login handler
 * itself opens the OTP session (`requiresOtp: true` branch in login.ts). Re-
 * issue-on-timeout is deferred to v2.
 */
import { commitVerifiedSession } from "./commitVerifiedSession.js";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import { parseRetryAfterSeconds } from "../refresh/performRefresh.js";
import type { AuthSnapshot, Audience } from "../types.js";

export interface CompleteOtpArgs {
  otpSessionToken: string;
  code: string;
  apiBaseUrl: string;
  /**
   * Target audience (admin | mobile) — REQUIRED by the backend OtpVerifyRequest.
   * Omitting it 400s and the OTP login step fails. useAuth injects it from app
   * context (same as login / invite-accept).
   */
  audience: Audience;
}

interface OtpSuccessBody {
  accessToken: string;
  // Optional — real backend returns tokens-only on `/auth/otp/verify` same as
  // Response A. MSW fixtures include a `user` object for back-compat.
  refreshToken?: string;
  user?: {
    id: string;
    email?: string | null;
    displayName: string;
    roles?: string[];
    username?: string | null;
  };
  mustChangePassword?: boolean;
}

export async function completeOtp({
  otpSessionToken,
  code,
  apiBaseUrl,
  audience,
}: CompleteOtpArgs): Promise<{ snapshot: AuthSnapshot }> {
  const res = await fetch(apiBaseUrl + "/api/v1/auth/otp/verify", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ otpSessionToken, code, audience }),
  });
  const body = (await res.json().catch(() => ({}))) as
    | OtpSuccessBody
    | { errorCode?: string; title?: string };

  if (!res.ok) {
    const e = body as { errorCode?: string; title?: string };
    throw Object.assign(new Error(e.title ?? "OTP verification failed"), {
      // The RFC 7807 title as a real property — consumers branch on it
      // (message alone is not introspectable as "the server copy").
      title: e.title,
      status: res.status,
      // 429 is bodyless: too many wrong codes for this person, not a bad code.
      errorCode: e.errorCode ?? (res.status === 429 ? "AUTH_RATE_LIMITED" : "OTP_INVALID"),
      correlationId: res.headers.get("x-correlation-id"),
      retryAfterSeconds: parseRetryAfterSeconds(res),
      isApiError: true,
    });
  }

  const success = body as OtpSuccessBody;
  // Validate the token at the shared security boundary BEFORE trusting it —
  // fails closed (stores nothing) on a garbage / expired / wrong-audience token.
  const snapshot = await commitVerifiedSession({
    accessToken: success.accessToken,
    expectedAudience: audience,
    apiBaseUrl,
    bodyUser: success.user,
    mustChangePassword: success.mustChangePassword === true,
    correlationId: res.headers.get("x-correlation-id"),
  });
  return { snapshot };
}

/**
 * POST /api/v1/auth/otp/resend (CC-87 / be-094) — issue a new OTP without
 * making the user re-enter their password.
 *
 * Backend returns a NEW `otpSessionToken`; the previous token stays valid
 * until natural expiry. Caller swaps the token into local form state so the
 * next verify uses the fresh one.
 */
export interface ResendOtpArgs {
  otpSessionToken: string;
  apiBaseUrl: string;
}

export interface ResendOtpResult {
  otpSessionToken: string;
  ttl: number;
  channel: "email" | "sms";
}

export async function performOtpResend({
  otpSessionToken,
  apiBaseUrl,
}: ResendOtpArgs): Promise<ResendOtpResult> {
  const res = await fetch(apiBaseUrl + "/api/v1/auth/otp/resend", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ otpSessionToken }),
  });
  const body = (await res.json().catch(() => ({}))) as
    | { otpSessionToken?: string; ttl?: number; channel?: string }
    | { errorCode?: string; title?: string };

  if (!res.ok) {
    const e = body as { errorCode?: string; title?: string };
    const fallback =
      res.status === 429 ? "AUTH_RATE_LIMITED" : res.status === 410 ? "OTP_EXPIRED" : "OTP_INVALID";
    throw Object.assign(new Error(e.title ?? "OTP resend failed"), {
      // The RFC 7807 title as a real property — consumers branch on it
      // (message alone is not introspectable as "the server copy").
      title: e.title,
      status: res.status,
      errorCode: e.errorCode ?? fallback,
      correlationId: res.headers.get("x-correlation-id"),
      isApiError: true,
    });
  }
  const success = body as { otpSessionToken?: string; ttl?: number; channel?: string };
  return {
    otpSessionToken: success.otpSessionToken ?? otpSessionToken,
    ttl: typeof success.ttl === "number" ? success.ttl : 0,
    channel: success.channel === "sms" ? "sms" : "email",
  };
}
